import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import CustomTourModal from '../components/CustomTourModal';
import Footer from '../components/Footer';

const Categories = () => {
    const [customTourModalOpen, setCustomTourModalOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/categories');
                setCategories(response.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCategories();
    }, []);

    return (
        <>
            <div>
                <Navigation />

                {/* Hero Section */}
                <section className="relative mt-20 overflow-hidden bg-gradient-to-r from-brand-blue/80 to-brand-teal/60 text-white py-24">
                    <div className="blob bg-brand-green w-96 h-96 top-20 left-10"></div>
                    <div className="blob bg-brand-teal w-80 h-80 bottom-20 right-20" style={{ animationDelay: '2s' }}></div>

                    <div className="absolute inset-0">
                        <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&h=600&fit=crop" alt="Tour Types" className="w-full h-full object-cover opacity-20" />
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">Choose Your Tour Type</h1>
                        <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto mb-8">From group adventures to personalized experiences, we offer tour types tailored to every traveler's needs</p>
                        <button
                            onClick={() => setCustomTourModalOpen(true)}
                            className="bg-gradient-to-r from-brand-green to-brand-teal text-white px-10 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition transform hover:scale-110 pulse-animation"
                        >
                            <i className="fas fa-magic mr-2"></i>Create Your Custom Tour
                        </button>
                    </div>
                </section>

                {/* Tour Types Grid */}
                <section className="py-20 bg-gray-50 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                        <div className="blob bg-brand-green w-96 h-96 top-40 left-40"></div>
                        <div className="blob bg-brand-cyan w-80 h-80 bottom-40 right-40" style={{ animationDelay: '3s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">Our Tour Types</h2>
                            <p className="text-xl text-gray-600">Select the perfect tour type for your travel style</p>
                        </div>

                        {loading ? (
                            <div className="text-center py-20">
                                <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
                                <p className="text-gray-600">Loading categories...</p>
                            </div>
                        ) : categories.length === 0 ? (
                            <div className="text-center py-20">
                                <i className="fas fa-box-open text-4xl text-gray-400 mb-4"></i>
                                <p className="text-gray-600">No categories available at the moment.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                                {categories.map((category) => (
                                    <div key={category._id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover-lift card-shine cursor-pointer">
                                        <div className="relative h-64 overflow-hidden hover-zoom">
                                            <img
                                                src={category.image ? (category.image.startsWith('http') ? category.image : `${API_URL}${category.image}`) : 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&h=800&fit=crop'}
                                                alt={category.name}
                                                className="w-full h-full object-cover transition duration-700"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                                <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
                                                <p className="text-gray-200 text-sm">{category.description?.substring(0, 60)}...</p>
                                            </div>
                                        </div>
                                        <div className="p-6">
                                            {category.features && category.features.length > 0 ? (
                                                <div className="mb-4">
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        {category.features.slice(0, 3).map((feature, idx) => (
                                                            <li key={idx}><i className="fas fa-check text-brand-teal mr-2"></i>{feature}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : (
                                                <div className="mb-4 h-16">
                                                    {/* Spacer if no features */}
                                                </div>
                                            )}
                                            <Link
                                                to={`/tours?category=${category.id || category._id}`}
                                                className="w-full block text-center bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-105"
                                            >
                                                <i className="fas fa-compass mr-2"></i>View Tours
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* Custom Tour CTA Section */}
                <section className="py-20 gradient-animate text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="blob bg-white w-96 h-96 top-20 left-20"></div>
                        <div className="blob bg-white w-80 h-80 bottom-20 right-20" style={{ animationDelay: '3s' }}></div>
                    </div>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <i className="fas fa-magic text-6xl mb-6 float-animation"></i>
                        <h2 className="text-4xl sm:text-5xl font-bold mb-4">Can't Find What You're Looking For?</h2>
                        <p className="text-xl text-green-100 mb-8">Create a custom tour tailored exactly to your preferences, budget, and schedule</p>
                        <button
                            onClick={() => setCustomTourModalOpen(true)}
                            className="bg-white text-brand-teal px-10 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition transform hover:scale-110"
                        >
                            <i className="fas fa-wand-magic mr-2"></i>Build Your Custom Tour
                        </button>
                    </div>
                </section>

                <Footer />

                <CustomTourModal
                    isOpen={customTourModalOpen}
                    onClose={() => setCustomTourModalOpen(false)}
                />
            </div>
        </>
    );
};

export default Categories;
